import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Shield, RefreshCw, AlertTriangle, AlertCircle, CheckCircle, FileText, Sparkles, Wrench, Copy, Globe, Clock, Code } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Acronym } from '../components/shared/AcronymTooltip';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ADACompliancePage = () => {
  const [scanning, setScanning] = useState(false);
  const [scanData, setScanData] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [fixDialogOpen, setFixDialogOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [scanUrl, setScanUrl] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/ada/scan`).then(r => setScanData(r.data)).catch(() => {}),
      axios.get(`${API_URL}/api/ada/history`).then(r => setScanHistory(r.data.scans || [])).catch(() => {})
    ]).finally(() => setLoading(false));
  }, []);

  const handleScan = async () => {
    if (!scanUrl.trim()) { toast.error('Enter a URL to scan'); return; }
    setScanning(true);
    try {
      const res = await axios.post(`${API_URL}/api/ada/scan-url`, { url: scanUrl.trim() });
      setScanData(res.data);
      toast.success(`Scan complete! Score: ${res.data.score}/100`);
      const histRes = await axios.get(`${API_URL}/api/ada/history`);
      setScanHistory(histRes.data.scans || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleShowFix = (issue) => { setSelectedIssue(issue); setFixDialogOpen(true); };
  const copyFix = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };
  const getScoreColor = (s) => s >= 80 ? 'text-emerald-brand' : s >= 50 ? 'text-amber-500' : 'text-red-500';
  const getScoreBg = (s) => s >= 80 ? 'bg-emerald-brand' : s >= 50 ? 'bg-amber-500' : 'bg-red-500';
  const getStatusIcon = (st) => st === 'pass' ? <CheckCircle className="h-5 w-5 text-emerald-brand" /> : st === 'partial' ? <AlertCircle className="h-5 w-5 text-amber-500" /> : <AlertTriangle className="h-5 w-5 text-red-500" />;

  const severityConfig = {
    critical: { color: 'text-red-500 bg-red-50 border-red-200', badge: 'bg-red-500' },
    warning: { color: 'text-amber-500 bg-amber-50 border-amber-200', badge: 'bg-amber-500' },
    info: { color: 'text-blue-500 bg-blue-50 border-blue-200', badge: 'bg-blue-500' },
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 text-emerald-brand animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="ada-compliance-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary"><Acronym term="ADA" /> Compliance<PageInfoTooltip pageKey="ada-compliance" /></h1>
          <p className="text-gray-medium">Real WCAG 2.1 AA accessibility scanner</p>
        </div>
      </div>

      {/* URL Scanner */}
      <Card className="bg-white border-gray-border">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Label className="text-sm font-medium mb-1 block">Website URL</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-medium" />
                  <Input
                    placeholder="https://example.com"
                    value={scanUrl}
                    onChange={(e) => setScanUrl(e.target.value)}
                    className="pl-9"
                    data-testid="ada-url-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleScan()}
                  />
                </div>
                <Button className="bg-emerald-brand hover:bg-emerald-hover gap-2" onClick={handleScan} disabled={scanning} data-testid="run-scan-btn">
                  {scanning ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
                  {scanning ? 'Scanning...' : 'Scan'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {scanData && scanData.score !== undefined && scanData.last_scan && (
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white border border-gray-border w-full sm:w-auto overflow-x-auto flex-nowrap justify-start">
            <TabsTrigger value="overview" className="text-xs sm:text-sm whitespace-nowrap">Overview</TabsTrigger>
            <TabsTrigger value="issues" data-testid="tab-issues" className="text-xs sm:text-sm whitespace-nowrap">Issues ({scanData.total_issues || 0})</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history" className="text-xs sm:text-sm whitespace-nowrap">History</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-white border-gray-border">
                <CardContent className="p-5 text-center">
                  <div className={cn("text-5xl font-heading font-bold mb-2", getScoreColor(scanData.score))}>{scanData.score}</div>
                  <Progress value={scanData.score} className="mb-2" />
                  <p className="text-sm text-gray-medium">Compliance Score</p>
                  {scanData.url && <p className="text-xs text-gray-medium mt-1 truncate">{scanData.url}</p>}
                </CardContent>
              </Card>
              <Card className="bg-white border-gray-border md:col-span-3">
                <CardHeader><CardTitle className="text-lg font-heading">WCAG 2.1 AA Criteria</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {scanData.wcag_criteria?.map((c, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-off-white rounded-lg">
                        {getStatusIcon(c.status)}
                        <div>
                          <p className="font-medium text-sm text-text-primary">{c.name}</p>
                          <p className="text-xs text-gray-medium">{c.issues} issues</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
            {scanData.page_stats && (
              <Card className="bg-white border-gray-border">
                <CardHeader><CardTitle className="text-lg font-heading">Page Analysis</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {[
                      { label: 'Images', value: scanData.page_stats.total_images },
                      { label: 'Links', value: scanData.page_stats.total_links },
                      { label: 'Form Inputs', value: scanData.page_stats.total_inputs },
                      { label: 'Headings', value: scanData.page_stats.total_headings },
                      { label: 'Has Title', value: scanData.page_stats.has_title ? 'Yes' : 'No' },
                      { label: 'Has Lang', value: scanData.page_stats.has_lang ? 'Yes' : 'No' },
                    ].map((s, i) => (
                      <div key={i} className="p-3 bg-off-white rounded-lg text-center">
                        <p className="text-lg font-semibold text-text-primary">{s.value}</p>
                        <p className="text-xs text-gray-medium">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="issues" className="space-y-3">
            {scanData.issues?.length > 0 ? scanData.issues.map((issue, i) => {
              const config = severityConfig[issue.severity] || severityConfig.info;
              return (
                <Card key={i} className={cn("border-2", config.color)} data-testid={`ada-issue-${issue.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge variant="outline" className="text-xs">{issue.criterion}</Badge>
                          <Badge className={cn("text-xs text-white capitalize", config.badge)}>{issue.severity}</Badge>
                          <span className="text-xs text-gray-medium">{issue.affected} element(s)</span>
                        </div>
                        <p className="font-medium text-text-primary">{issue.description}</p>
                        {issue.elements?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {issue.elements.slice(0, 3).map((el, j) => (
                              <div key={j} className="flex items-start gap-1.5">
                                <Code className="h-3.5 w-3.5 text-gray-medium flex-shrink-0 mt-0.5" />
                                <code className="text-xs text-gray-medium bg-gray-100 px-1.5 py-0.5 rounded break-all">{el}</code>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button size="sm" className="bg-emerald-brand hover:bg-emerald-hover gap-1 flex-shrink-0" onClick={() => handleShowFix(issue)} data-testid={`fix-issue-${issue.id}`}>
                        <Wrench className="h-3.5 w-3.5" /> AI Fix
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            }) : (
              <Card className="bg-white border-gray-border">
                <CardContent className="p-8 text-center">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-emerald-brand" />
                  <p className="text-text-primary font-medium">No issues found!</p>
                  <p className="text-sm text-gray-medium">This page passes all checked WCAG 2.1 AA criteria.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-3">
            {scanHistory.length > 0 ? scanHistory.map((scan, i) => (
              <Card key={i} className="bg-white border-gray-border">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg", getScoreBg(scan.score))}>
                      {scan.score}
                    </div>
                    <div>
                      <p className="font-medium text-text-primary truncate max-w-[300px]">{scan.url}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-medium">
                        <Clock className="h-3 w-3" />
                        {new Date(scan.last_scan).toLocaleString()}
                        <span>{scan.total_issues} issues</span>
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setScanUrl(scan.url)}>Re-scan</Button>
                </CardContent>
              </Card>
            )) : (
              <Card className="bg-white border-gray-border">
                <CardContent className="p-8 text-center">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-medium">No scan history yet</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}

      {(!scanData || !scanData.last_scan) && (
        <Card className="bg-white border-gray-border">
          <CardContent className="p-12 text-center">
            <Shield className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-lg font-heading font-semibold text-text-primary mb-2">Scan Your Website for Accessibility</h3>
            <p className="text-gray-medium max-w-md mx-auto">Enter a URL above to run a real WCAG 2.1 AA compliance scan. We check for missing alt text, heading structure, form labels, language attributes, duplicate IDs, and more.</p>
          </CardContent>
        </Card>
      )}

      {/* AI Fix Dialog */}
      <Dialog open={fixDialogOpen} onOpenChange={setFixDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-emerald-brand" /> AI Fix Suggestion
            </DialogTitle>
          </DialogHeader>
          {selectedIssue && (
            <div className="space-y-4 mt-2">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm font-medium text-red-700">Issue: {selectedIssue.description}</p>
                <p className="text-xs text-red-600 mt-1">WCAG {selectedIssue.criterion} | {selectedIssue.affected} element(s)</p>
              </div>
              {selectedIssue.elements?.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-gray-medium mb-2">Affected Elements:</p>
                  {selectedIssue.elements.slice(0, 3).map((el, i) => (
                    <code key={i} className="block text-xs text-gray-600 bg-white p-1.5 rounded mb-1 break-all">{el}</code>
                  ))}
                </div>
              )}
              <div className="bg-emerald-brand/5 border border-emerald-brand/20 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-sm font-medium text-emerald-brand">Recommended Fix</p>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => copyFix(selectedIssue.fix_suggestion)}>
                    <Copy className="h-3 w-3 mr-1" /> Copy
                  </Button>
                </div>
                <p className="text-sm text-text-primary leading-relaxed">{selectedIssue.fix_suggestion}</p>
              </div>
              <Button className="w-full bg-emerald-brand hover:bg-emerald-hover" onClick={() => { setFixDialogOpen(false); toast.success('Fix applied!'); }}>
                Mark as Fixed
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ADACompliancePage;
